import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BiometricCatalogService, type BiometricPrice, type BiometricPriceCreate, type BiometricPriceUpdate } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

export const useBiometricPrices = () =>
  useQuery({
    queryKey: ["biometric-prices"],
    queryFn: () => fetchAll<BiometricPrice>(BiometricCatalogService.getApiBiometricPrices),
    staleTime: 5 * 60_000,
  });

export const useCreateBiometricPrice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: BiometricPriceCreate) => {
      const res = await BiometricCatalogService.postApiBiometricPrices({ body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as BiometricPrice;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["biometric-prices"] }),
  });
};

export const useUpdateBiometricPrice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: BiometricPriceUpdate }) => {
      const res = await BiometricCatalogService.putApiBiometricPricesById({ path: { id }, body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as BiometricPrice;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["biometric-prices"] }),
  });
};

export const useDeleteBiometricPrice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await BiometricCatalogService.deleteApiBiometricPricesById({ path: { id }, headers: getAuthHeaders() });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["biometric-prices"] }),
  });
};
